<script setup lang="ts">
/**
 * A choice among fixed options in the application form (design prototype `bf-seg`, `bf-chips`):
 * exactly one as a segment of radio buttons, which the keyboard walks with the arrow keys, or
 * several as chips of checkboxes, which carry a tick once chosen. Each input sits inside its
 * label, out of sight; the label is the surface and shows the focus the keyboard gives the input,
 * as the kinds' segment of `InquiryForm` does. The group is named by its visible label, says it is
 * required with an asterisk, carries its error below and can take the focus itself, so the form
 * can lead there after a failed send. `blur` fires once the focus leaves the group.
 */
import { useId } from "vue";

export type ChoiceOption = { value: string; label: string };

const props = defineProps<{
  label: string;
  options: readonly ChoiceOption[];
  /** Several at once (chips); otherwise exactly one (segment). */
  multiple?: boolean | undefined;
  required?: boolean | undefined;
  /** In normal weight after the label, e.g. "(mehrere möglich)". */
  hint?: string | undefined;
  error?: string | undefined;
}>();

const emit = defineEmits<{ blur: [] }>();

/** The chosen code (segment) or codes (chips). */
const model = defineModel<string | readonly string[] | undefined>();

const id = useId();

const chosen = (value: string) =>
  props.multiple ? Array.isArray(model.value) && model.value.includes(value) : model.value === value;

/** A radio button sets its code; a checkbox adds or removes its own, in the order of the options. */
function choose(value: string, checked: boolean) {
  if (!props.multiple) {
    model.value = value;
    return;
  }
  const current: readonly string[] = Array.isArray(model.value) ? model.value : [];
  model.value = props.options
    .map((option) => option.value)
    .filter((code) => (code === value ? checked : current.includes(code)));
}

function onFocusOut(event: FocusEvent) {
  const group = event.currentTarget as HTMLElement;
  if (!group.contains(event.relatedTarget as Node | null)) emit("blur");
}

/** The chosen option filled dark, the others recessed, a red edge while the choice is missing; whole class names (AGENTS.md). */
const optionClass = {
  on: "bg-solid text-solid-fg",
  off: "bg-fill text-text-body hover:bg-fill-2",
  invalid: "shadow-[inset_0_0_0_1.5px_var(--color-err-text)]",
};
const optionBase =
  "rounded-control text-ui has-focus-visible:outline-focus relative inline-flex min-h-(--size-field) cursor-pointer items-center gap-1.5 px-4 font-semibold transition-colors has-focus-visible:outline-2 has-focus-visible:outline-offset-2 pointer-coarse:min-h-(--size-button)";
</script>

<template>
  <div class="flex flex-col gap-1.5" @focusout="onFocusOut">
    <span :id="`${id}-label`" class="text-ui text-text-head font-semibold" data-choice-label>
      {{ props.label }}
      <span v-if="props.required" class="text-bad-text" aria-hidden="true">*</span>
      <span v-if="props.hint" class="font-normal">{{ props.hint }}</span>
    </span>
    <div
      :role="props.multiple ? 'group' : 'radiogroup'"
      :aria-labelledby="`${id}-label`"
      :aria-required="(props.required && !props.multiple) || undefined"
      :aria-invalid="props.error ? 'true' : undefined"
      :aria-describedby="props.error ? `${id}-error` : undefined"
      tabindex="-1"
      class="rounded-control focus-visible:outline-focus flex flex-wrap gap-2 focus-visible:outline-2 focus-visible:outline-offset-2"
    >
      <label
        v-for="option in props.options"
        :key="option.value"
        :class="[
          optionBase,
          chosen(option.value) ? optionClass.on : optionClass.off,
          props.error ? optionClass.invalid : '',
        ]"
        :data-option="option.value"
      >
        <input
          :type="props.multiple ? 'checkbox' : 'radio'"
          class="sr-only"
          :name="id"
          :value="option.value"
          :checked="chosen(option.value)"
          @change="choose(option.value, ($event.target as HTMLInputElement).checked)"
        />
        <!-- Phosphor "check" of the prototype's chips -->
        <svg
          v-if="props.multiple && chosen(option.value)"
          class="size-3.5 flex-none"
          viewBox="0 0 256 256"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            d="M229.66 77.66l-128 128a8 8 0 0 1-11.32 0l-56-56a8 8 0 0 1 11.32-11.32L96 188.69L218.34 66.34a8 8 0 0 1 11.32 11.32"
          />
        </svg>
        {{ option.label }}
      </label>
    </div>
    <span v-if="props.error" :id="`${id}-error`" role="alert" class="text-meta text-bad-text">{{ props.error }}</span>
  </div>
</template>
